import { useRouter } from 'expo-router';
import React from 'react';
import { Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { LiveBadge } from '@/components/ui/LiveBadge';
import type { FixtureWithRefs } from '@/lib/data/provider';
import { formatKickoffTime } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeTeamName } from '@/lib/i18n/footballNames';

import { AnimatedScore } from './AnimatedScore';
import { TeamCrest } from './TeamCrest';

function TeamSide({
  name,
  logo,
  align,
}: {
  name: string;
  logo: string | null;
  align: 'start' | 'end';
}) {
  return (
    <View
      className={`flex-1 flex-row items-center gap-2 ${
        align === 'end' ? 'justify-end' : 'justify-start'
      }`}
    >
      {align === 'end' ? (
        <AppText variant="caption" weight="medium" className="shrink text-end text-ink" numberOfLines={1}>
          {name}
        </AppText>
      ) : null}
      <TeamCrest uri={logo} name={name} size="sm" />
      {align === 'start' ? (
        <AppText variant="caption" weight="medium" className="shrink text-start text-ink" numberOfLines={1}>
          {name}
        </AppText>
      ) : null}
    </View>
  );
}

export function FixtureCard({ fixture }: { fixture: FixtureWithRefs }) {
  const router = useRouter();
  const { t } = useTranslation();
  const { numeralOptions, locale } = useLocale();
  const kickoffDate = fixture.kickoff_at ? new Date(fixture.kickoff_at) : null;
  const kickoff = kickoffDate && !Number.isNaN(kickoffDate.getTime()) ? kickoffDate : null;

  const center = (() => {
    if (fixture.status === 'live') {
      return (
        <View className="items-center gap-1">
          <AnimatedScore home={fixture.home_score ?? 0} away={fixture.away_score ?? 0} />
          <LiveBadge minute={fixture.minute} />
        </View>
      );
    }
    if (fixture.status === 'finished') {
      return (
        <View className="items-center gap-0.5">
          <AnimatedScore home={fixture.home_score ?? 0} away={fixture.away_score ?? 0} />
          <AppText variant="micro" className="text-muted">
            {t('common.finished')}
          </AppText>
        </View>
      );
    }
    if (fixture.status === 'postponed') {
      return (
        <AppText variant="micro" weight="medium" className="text-draw">
          {t('common.postponed')}
        </AppText>
      );
    }
    return (
      <AppText variant="body" weight="semibold" tabular className="text-ink">
        {kickoff ? formatKickoffTime(kickoff, numeralOptions) : '—'}
      </AppText>
    );
  })();

  return (
    <Pressable
      accessibilityRole="button"
      onPress={() => router.push(`/match/${fixture.id}`)}
      className="flex-row items-center gap-3 px-4 py-3.5 active:bg-surface-2/60"
    >
      <TeamSide
        name={localizeTeamName(fixture.home_team, locale)}
        logo={fixture.home_team?.logo_url ?? null}
        align="end"
      />
      <View className="w-[88px] items-center">{center}</View>
      <TeamSide
        name={localizeTeamName(fixture.away_team, locale)}
        logo={fixture.away_team?.logo_url ?? null}
        align="start"
      />
    </Pressable>
  );
}
